import { formatShortInTimezone } from '../../../utils/formatInTimezone';

/*
 * ТЗ V5 №11: цвета состояния камеры на диаграмме «Контроля».
 *
 * Статус сегмента приходит с сервера. Всё, что не покрыто сегментами или
 * пришло с неизвестным статусом, — «Нет данных» (серый): отсутствие данных
 * нельзя рисовать зелёным.
 */
export const TIMELINE_STATUS = {
  ONLINE: { label: 'Онлайн', tone: 'online' },
  TECHNICAL_DOWNTIME: { label: 'Технический простой', tone: 'offline' },
  MANUALLY_STOPPED: { label: 'Остановлена вручную', tone: 'stopped' },
  NO_DATA: { label: 'Нет данных', tone: 'unknown' },
};

export const TIMELINE_LEGEND = ['ONLINE', 'TECHNICAL_DOWNTIME', 'MANUALLY_STOPPED', 'NO_DATA']
  .map((key) => ({ key, ...TIMELINE_STATUS[key] }));

const toMs = (iso) => {
  const value = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(value) ? value : null;
};

const describe = (status) => TIMELINE_STATUS[status] || TIMELINE_STATUS.NO_DATA;

/**
 * Сегменты строки в процентах от интервала [fromAt, toAt]. Сегменты
 * обрезаются по интервалу, дыры между ними заполняются «Нет данных».
 */
export const layoutTimelineRow = (segments, fromAt, toAt) => {
  const start = toMs(fromAt);
  const end = toMs(toAt);
  if (start == null || end == null || end <= start) return [];
  const span = end - start;

  const sorted = (Array.isArray(segments) ? segments : [])
    .map((segment) => ({
      status: TIMELINE_STATUS[segment?.status] ? segment.status : 'NO_DATA',
      inferred: segment?.evidence_basis === 'inferred_from_continuity',
      from: Math.max(start, toMs(segment?.from_at) ?? start),
      to: Math.min(end, toMs(segment?.to_at) ?? end),
    }))
    .filter((segment) => segment.to > segment.from)
    .sort((a, b) => a.from - b.from);

  const out = [];
  let cursor = start;
  const push = (status, from, to, inferred = false) => {
    if (to <= from) return;
    out.push({
      status,
      inferred,
      ...describe(status),
      fromMs: from,
      toMs: to,
      left: ((from - start) / span) * 100,
      width: ((to - from) / span) * 100,
    });
  };

  sorted.forEach((segment) => {
    const from = Math.max(segment.from, cursor);
    if (from > cursor) push('NO_DATA', cursor, from);
    push(segment.status, from, segment.to, segment.inferred);
    cursor = Math.max(cursor, segment.to);
  });
  push('NO_DATA', cursor, end);
  return out;
};

/** Подсказка сегмента: «Онлайн · 24.10 00:00:00–25.10 12:00:00» в поясе камеры. */
export const describeTimelineSegment = (segment, timeZone) => {
  const label = segment.inferred ? `${segment.label}, расчётно` : segment.label;
  const from = formatShortInTimezone(new Date(segment.fromMs).toISOString(), timeZone);
  const to = formatShortInTimezone(new Date(segment.toMs).toISOString(), timeZone);
  return `${label} · ${from}–${to}`;
};

/** Доли состояний строки для подписи доступности: «Онлайн 80%, Нет данных 20%». */
export const summarizeTimelineRow = (layout) => {
  const totals = new Map();
  layout.forEach((segment) => {
    totals.set(segment.label, (totals.get(segment.label) || 0) + segment.width);
  });
  return [...totals]
    .filter(([, width]) => width >= 0.5)
    .sort((a, b) => b[1] - a[1])
    .map(([label, width]) => `${label} ${Math.round(width)}%`)
    .join(', ');
};
